import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Fingerprint,
  AlertTriangle,
  Shield,
  BarChart3,
  Layers,
  ArrowRight,
  CheckCircle,
  Code,
  BookOpen,
  TestTube,
  Webhook,
  FileJson,
  Package,
  Lock,
  Copy,
  Check,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { LandingHeader } from './LandingHeader';

interface LandingPageProps {
  onSignIn: () => void;
  onSignUp: () => void;
}

const apiModules: ReadonlyArray<{
  icon: typeof Fingerprint;
  title: string;
  description: string;
}> = [
  {
    icon: Fingerprint,
    title: 'Identity Verification',
    description: 'Verify customers using document checks, biometric matching, liveness, national ID, passport, and risk signals.',
  },
  {
    icon: Shield,
    title: 'AML Screening',
    description: 'Screen customers and entities against sanctions, PEP, watchlists, adverse media, and compliance risk indicators.',
  },
  {
    icon: AlertTriangle,
    title: 'Fraud Intelligence',
    description: 'Detect suspicious activity using transaction events, behavioral signals, device risk, velocity checks, and case escalation.',
  },
  {
    icon: BarChart3,
    title: 'Financial Connectivity',
    description: 'Connect bank and mobile money data for account verification, income analysis, transaction intelligence, and financial risk review.',
  },
  {
    icon: Layers,
    title: 'Decision Intelligence',
    description: 'Generate explainable risk decisions with status, score, review reasons, and audit-ready evidence.',
  },
  {
    icon: Webhook,
    title: 'Webhooks',
    description: 'Receive real-time updates for verification status, AML matches, fraud alerts, and risk decision changes.',
  },
];

const integrationSteps: ReadonlyArray<{ title: string; body: string }> = [
  { title: 'Request sandbox access', body: 'Create a test workspace with sandbox-only credentials.' },
  { title: 'Generate a test API key', body: 'Use a safe test key such as wdya_test_xxxxxxxxxxxxx.' },
  { title: 'Make your first API call', body: 'Send a verification, screening, or fraud event to Wardya.' },
  { title: 'Receive a decision', body: 'Get a status, risk score, decision, and review reason.' },
  { title: 'Configure webhooks', body: 'Subscribe to verification, AML, fraud, and risk events.' },
  { title: 'Review results', body: 'Use the Wardya Console to review alerts, cases, and evidence.' },
];

const trustItems: readonly string[] = [
  'API key authentication',
  'Webhook signature verification',
  'Encryption in transit',
  'Encryption at rest',
  'Audit-ready activity history',
  'Role-based access control',
  'Tenant-level isolation',
  'Data residency options',
  'Enterprise onboarding controls',
];

const resources: ReadonlyArray<{
  title: string;
  to: string;
  description: string;
  icon: typeof BookOpen;
}> = [
  {
    title: 'Quickstart',
    to: '/developers/quickstart',
    description: 'Build your first Wardya sandbox workflow in minutes.',
    icon: BookOpen,
  },
  {
    title: 'Sandbox',
    to: '/developers/sandbox',
    description: 'Request access, test keys, and run synthetic responses.',
    icon: TestTube,
  },
  {
    title: 'Security',
    to: '/developers/security',
    description: 'Review public security and compliance summaries.',
    icon: Lock,
  },
  {
    title: 'API Reference',
    to: '/developers/api-reference',
    description: 'Review endpoints, examples, errors, and rate limits.',
    icon: Code,
  },
  {
    title: 'SDKs',
    to: '/developers/sdks',
    description: 'Use JavaScript, C#, Python, and Go integration helpers.',
    icon: Package,
  },
  {
    title: 'Webhooks',
    to: '/developers/webhooks',
    description: 'Receive verification, risk, alert, and case events.',
    icon: Webhook,
  },
  {
    title: 'Postman',
    to: '/developers/postman',
    description: 'Download collections for sandbox API testing.',
    icon: FileJson,
  },
];

const heroChips: readonly string[] = [
  'API-first platform',
  'Sandbox-ready',
  'Secure webhooks',
  'Multi-tenant support',
  'Bank, fintech, government, and compliance use cases',
];

const sampleRequest = `await fetch("/api/identity-verifications", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    tenantId: "tenant_demo",
    customer: {
      firstName: "Amanuel",
      lastName: "Tesfaye",
      country: "ET"
    },
    checks: ["document", "face_match", "liveness", "aml"]
  })
});`;

const sampleResponse = `{
  "verificationId": "vrf_demo_100245",
  "status": "review_required",
  "riskScore": 72,
  "decision": "manual_review",
  "reasons": [
    "document_quality_low",
    "aml_possible_match"
  ]
}`;

export function LandingPage({ onSignIn, onSignUp: _onSignUp }: LandingPageProps) {
  const [copied, setCopied] = useState<'request' | 'response' | null>(null);

  const copyCode = (code: string, id: 'request' | 'response') => {
    void navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <main className="min-h-screen bg-white text-slate-950">
      <LandingHeader onSignIn={onSignIn} />

      <section className="relative overflow-hidden pt-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Badge className="mb-5 border border-blue-100 bg-blue-50 px-4 py-2 text-blue-900">
              Wardya developer platform
            </Badge>
            <h1 className="max-w-4xl text-5xl font-semibold tracking-tight text-slate-950 md:text-6xl md:leading-[1.05]">
              Build Trust Infrastructure Into Your Product
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">
              Wardya provides secure APIs for identity verification, AML screening, fraud detection, financial connectivity, and decision intelligence built for regulated institutions, fintechs, banks, governments, and enterprise platforms.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/developers/quickstart">
                <Button className="bg-blue-900 px-5 py-3 text-white hover:bg-blue-800">
                  Start Quickstart
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/developers/api-reference">
                <Button variant="outline" className="px-5 py-3">View API Reference</Button>
              </Link>
              <Link to="/developers/sandbox">
                <Button variant="outline" className="px-5 py-3">Request Sandbox Access</Button>
              </Link>
            </div>
            <p className="mt-5 max-w-2xl text-sm leading-6 text-slate-500">
              Sandbox examples only. Production access and private integrations are enabled after onboarding and approval.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {heroChips.map((chip) => (
                <span key={chip} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  {chip}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="rounded-lg border border-slate-200 bg-slate-50 p-5 shadow-sm"
          >
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-100">API status: operational</span>
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-900 ring-1 ring-blue-100">Sandbox-ready</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">v1</span>
            </div>
            <div className="rounded-lg bg-slate-950 p-5 font-mono text-sm text-slate-200">
              <div className="mb-4 flex items-center gap-2 text-xs text-slate-400">
                <span className="h-3 w-3 rounded-full bg-red-400" />
                <span className="h-3 w-3 rounded-full bg-amber-400" />
                <span className="h-3 w-3 rounded-full bg-emerald-400" />
                <span className="ml-2">UI form payload</span>
                <button
                  type="button"
                  onClick={() => copyCode(sampleRequest, 'request')}
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs text-slate-300 hover:bg-white/10"
                >
                  {copied === 'request' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === 'request' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap leading-7"><code>{sampleRequest}</code></pre>
            </div>
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
              Keep Wardya API keys on your server. Browser UI should call your backend, and your backend should call Wardya with the secret sandbox or production key.
            </div>
            <div className="mt-5 rounded-lg bg-white p-4 ring-1 ring-slate-200">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Decision response</span>
                <button
                  type="button"
                  onClick={() => copyCode(sampleResponse, 'response')}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  {copied === 'response' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === 'response' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap text-sm leading-6 text-slate-800"><code>{sampleResponse}</code></pre>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-900">Core API modules</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">
              API modules
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {apiModules.map((card) => {
              const Icon = card.icon;
              return (
                <Card key={card.title} className="rounded-lg border-0 bg-white shadow-none ring-1 ring-slate-200">
                  <CardContent className="p-6">
                    <Icon className="h-6 w-6 text-blue-900" />
                    <h3 className="mt-5 font-semibold text-slate-950">{card.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{card.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-900">How integration works</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">
              Integration flow
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              Start with safe sandbox credentials, test the public API surface, then move to approved onboarding when you are ready for production access.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/developers/quickstart">
                <Button className="bg-blue-900 text-white hover:bg-blue-800">Open Quickstart</Button>
              </Link>
              <Link to="/developers/sandbox">
                <Button variant="outline">Sandbox guide</Button>
              </Link>
            </div>
          </div>
          <div className="space-y-3">
            {integrationSteps.map((step, index) => (
              <div key={step.title} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-900">
                  {index + 1}
                </div>
                <div>
                  <div className="font-medium text-slate-900">{step.title}</div>
                  <div className="mt-1 text-sm leading-6 text-slate-600">{step.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-200">Security</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight">Built for regulated environments</h2>
            <p className="mt-4 leading-8 text-slate-300">
              Wardya protects developer and customer workflows with API authentication, encrypted transport, webhook signing, audit logging, role-based access, tenant isolation, and enterprise deployment options.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {trustItems.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
                <CheckCircle className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />
                <span className="text-sm text-slate-200">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-900">Resources</p>
            <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">
              Public developer resources.
            </h2>
          </div>
          <Link to="/developers/quickstart">
            <Button className="bg-slate-950 text-white hover:bg-slate-800">Open Quickstart</Button>
          </Link>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {resources.map((resource) => {
            const Icon = resource.icon;
            const inner = (
              <Card className="h-full rounded-lg border border-slate-200 bg-white shadow-none transition hover:border-blue-200 hover:bg-blue-50/40">
                <CardContent className="p-6">
                  <Icon className="mb-3 h-5 w-5 text-blue-900" />
                  <h3 className="font-semibold text-slate-950">{resource.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{resource.description}</p>
                  <div className="mt-4 inline-flex items-center text-sm font-semibold text-blue-900">
                    Open
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            );

            return <Link key={resource.title} to={resource.to}>{inner}</Link>;
          })}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-14 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
              Start building with Wardya
            </h2>
            <p className="mt-2 text-slate-600">
              Use sandbox APIs to test identity verification, AML screening, fraud intelligence, financial connectivity, and decision automation before production onboarding.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/developers/sandbox">
              <Button className="bg-blue-900 text-white hover:bg-blue-800">Request Sandbox Access</Button>
            </Link>
            <Link to="/developers/support">
              <Button variant="outline">Contact Sales</Button>
            </Link>
            <Link to="/developers/api-reference">
              <Button variant="outline">API Reference</Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
